import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Badge,
  useTheme,
  Divider,
} from '@mui/material';
import {
  FileBadge,
  Users,
  Settings,
  FolderGit2,
  LayoutDashboard,
  LandPlot,
  BookText,
  FileText,
} from 'lucide-react';
import LogoutButton from '../LogoutButton';
import { useAuth } from '../../contexts/AuthContext';

export type AdminContentType =
  | 'users'
  | 'projects'
  | 'students'
  | 'settings'
  | 'region'
  | 'fair'
  | 'forms'
  | 'assignment'
  | 'projectScores'
  | 'rubrics';

interface SidebarProps {
  activeContent: AdminContentType;
  onContentChange: (content: AdminContentType) => void;
  pendingProjectsCount: number;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeContent,
  onContentChange,
  pendingProjectsCount,
}) => {
  const theme = useTheme();
  const { authStatus } = useAuth();
  const userName = authStatus.metadata?.firstName || 'Admin';

  const menuItems = [
    { id: 'projectScores' as const, label: 'Project Scores', icon: FileBadge },
    { id: 'students' as const, label: 'Students', icon: Users },
    { id: 'users' as const, label: 'User Management', icon: Users },
    { id: 'forms' as const, label: 'Form Management', icon: BookText },
    { id: 'rubrics' as const, label: 'Rubrics', icon: FileText },
    { id: 'assignment' as const, label: 'Assignments', icon: Users },
    { id: 'region' as const, label: 'Region Details', icon: LayoutDashboard },
    { id: 'fair' as const, label: 'Fair Management', icon: LandPlot },
    { id: 'settings' as const, label: 'Settings', icon: Settings },
  ];

  return (
    <Box
      sx={{
        width: 280,
        backgroundColor: 'background.paper',
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="primary" fontWeight="bold">
          Admin Dashboard
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Welcome back,
        </Typography>
        <Typography variant="h6" color="text.primary" fontWeight="bold">
          {userName}
        </Typography>
      </Box>

      <List sx={{ px: 2, flexGrow: 1 }}>
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={() => onContentChange('projects')}
            selected={activeContent === 'projects'}
            sx={{
              borderRadius: '12px',
              '&.Mui-selected': {
                backgroundColor: 'primary.lighter',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              },
            }}
          >
            <FolderGit2 size={20} />
            <ListItemText primary="Projects" sx={{ ml: 2 }} />
            {pendingProjectsCount > 0 && (
              <Badge
                badgeContent={pendingProjectsCount}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: theme.palette.error.main,
                  },
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
        {menuItems.map(({ id, label, icon: Icon }) => (
          <ListItem key={id} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => onContentChange(id)}
              selected={activeContent === id}
              sx={{
                borderRadius: '12px',
                '&.Mui-selected': {
                  backgroundColor: 'primary.lighter',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <Icon size={20} />

              <ListItemText primary={label} sx={{ ml: 2 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2 }}>
        <LogoutButton variant="outlined" color="primary" />
      </Box>
    </Box>
  );
};

export default Sidebar;
